import {useAppContext} from 'common/context/AppContext'
import {showError} from 'common/util/Util'
import {Select} from 'antd'
import {LabelInValueType} from 'rc-select/lib/Select'
import React, {useEffect, useState} from 'react'

export interface DictionarySelectorProps {
    value?: any
    onChange?: (value: any) => void
    style?: React.CSSProperties
    disabled?: boolean
    placeholder?: string
    mode?: 'multiple' | 'tags',
    multiple?: boolean
    valueName?: string
    showSearch?: boolean
    allowClear?: boolean
}

export interface DictionaryBaseSelectorProps<ID extends number | string, T extends {id?: ID, name?: string}>
    extends DictionarySelectorProps
{
    cacheKey: string
    loader: () => Promise<Array<T>>
    errorText?: string
    labelRender?: (props: LabelInValueType) => React.ReactElement
}

export const DictionarySelector = <ID extends number | string, T extends { id?: ID, name?: string }>(
    {
        cacheKey,
        loader,
        errorText,
        value,
        onChange,
        style,
        disabled,
        placeholder,
        mode,
        labelRender,
        valueName,
        showSearch = true,
        allowClear = true
    }: DictionaryBaseSelectorProps<ID, T>
) => {
    const [options, setOptions] = useState<any>([])
    const [loading, setLoading] = useState<boolean>(false)
    const appContext = useAppContext()

    useEffect(() => {
        let options = appContext.dictionaries.get(cacheKey)
        if (options) {
            setOptions(options)
        } else {
            setLoading(true)
            loader()
                .then(items => {
                    const newOptions = items
                        .map(item => ({label: item.name, value: valueName ? item[valueName] : item.id}))
                        .sort((item1, item2) => (item1.label ?? "").localeCompare(item2.label ?? ""))

                    appContext.dictionaries.set(cacheKey, newOptions)
                    setOptions(newOptions)
                })
                .catch(e => showError(appContext, errorText || e))
                .finally(() => setLoading(false))
        }
    }, [])

    return <Select
        options={options}
        loading={loading}
        value={value}
        onChange={onChange}
        showSearch={showSearch}
        optionFilterProp="label"
        style={style}
        allowClear={allowClear}
        disabled={disabled}
        placeholder={placeholder}
        mode={mode}
        labelRender={labelRender}
    />
}

export function enumToLabelValue(enumType) {
    return enumValueToLabelValue(Object.values(enumType))
}

export function enumValueToLabelValue(enumVaues:Array<any>) {
    return Promise.resolve(enumVaues.map(item => (
        {id: String(item), name: String(item)}
    )))
}
